/**
 * MethodologiesPanel — pestaña "Metodologías" del catálogo (OT-0055).
 *
 * Lista las metodologías visibles (las del producto y las del tenant) y,
 * para el admin, crea una nueva. Click en una tarjeta → detalle.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import type { Methodology } from '../../../api/methodologies'
import { useApiErrorMessage } from '../../../hooks/useApiErrorMessage'
import { useMethodologies } from '../../../hooks/useMethodologies'
import { MethodologyDefaultBadges, MethodologyStatusChip } from './MethodologyBadges'
import { MethodologyFormDialog } from './MethodologyFormDialog'
import { DEFAULT_LOCALE } from '../../../i18n/config'

const STATUS_ORDER: Record<Methodology['status'], number> = {
  published: 0,
  draft: 1,
  archived: 2,
}

export function MethodologiesPanel({ canManage }: { canManage: boolean }) {
  const { t, i18n } = useTranslation('templates')
  const navigate = useNavigate()
  const apiErrorMessage = useApiErrorMessage()
  const { methodologies, isLoading, error } = useMethodologies()
  const [createOpen, setCreateOpen] = useState(false)

  // Las del producto primero; luego por estado (archivadas al final) y nombre.
  const sorted = useMemo(
    () =>
      [...methodologies].sort(
        (a, b) =>
          Number(a.tenant_id !== null) - Number(b.tenant_id !== null) ||
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          a.name.localeCompare(b.name, i18n.language || DEFAULT_LOCALE),
      ),
    [methodologies, i18n.language],
  )

  return (
    <section>
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <p className="font-body text-sm text-ink-muted max-w-3xl">
          {t('methodologies.list.intro')}
        </p>
        {canManage ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 font-ui text-sm text-ink hover:bg-surface-muted transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('methodologies.list.new_button')}
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <p className="font-mono text-xs text-ink-subtle">
          {t('methodologies.list.loading')}
        </p>
      ) : error ? (
        <div className="rounded-md border border-critical/40 bg-critical-soft p-4 text-xs text-critical">
          {apiErrorMessage(error)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface p-10 text-center">
          <p className="font-body text-sm text-ink-muted">
            {t('methodologies.list.empty')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((m) => (
            <MethodologyCard key={m.id} methodology={m} />
          ))}
        </div>
      )}

      <MethodologyFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(created) =>
          void navigate({
            to: '/templates/methodologies/$methodologyId',
            params: { methodologyId: created.id },
          })
        }
      />
    </section>
  )
}

function MethodologyCard({ methodology }: { methodology: Methodology }) {
  const { t } = useTranslation('templates')
  return (
    <Link
      to="/templates/methodologies/$methodologyId"
      params={{ methodologyId: methodology.id }}
      className="bg-surface border border-line rounded-md p-4 hover:border-line-strong transition-colors flex flex-col"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-review-soft text-review">
          {methodology.tenant_id === null
            ? t('methodologies.owner_product')
            : t('methodologies.owner_tenant')}
        </span>
        <span className="font-mono text-[10px] text-ink-subtle">
          v{methodology.version}
        </span>
      </div>
      <h3 className="font-display text-base font-semibold text-ink leading-snug">
        {methodology.name}
      </h3>
      {methodology.description ? (
        <p className="mt-1 font-body text-xs text-ink-muted line-clamp-2 leading-snug">
          {methodology.description}
        </p>
      ) : null}
      <div className="mt-auto pt-3 border-t border-line/60 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-mono text-ink-subtle mr-auto">
          {methodology.code}
        </span>
        <MethodologyDefaultBadges methodology={methodology} />
        {methodology.status !== 'published' ? (
          <MethodologyStatusChip status={methodology.status} />
        ) : null}
      </div>
    </Link>
  )
}
